import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLocalizedDestinationData, LocalizedDestinationData } from '../data/localizedData';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { getTripsForDestination } from '../data/relationships';
import { RelatedTrips } from '../components/RelatedTrips';
import { usePageTitle } from '../hooks/usePageTitle';

export default function DestinationDetailsPage() {
  const { destinationId } = useParams<{ destinationId: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'map' | 'related'>('overview');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const [showGalleryArrows, setShowGalleryArrows] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { t } = useLanguage();
  
  const destinationData = destinationId ? useLocalizedDestinationData(destinationId) : null;

  // Function to translate additional details values
  const translateAdditionalDetail = (value: string): string => {
    if (!value) return value;
    
    // Handle altitude translations
    if (value === 'between_1460_and_2138_meters_above_sea_level') {
      return `${t('common.between')} 1460 ${t('common.meters')} ${t('common.andMeters')} 2138 ${t('common.meters')} ${t('common.aboveSeaLevel')}`;
    }
    if (value === '980_m_above_sea_level') {
      return `980 m ${t('common.aboveSeaLevel')}`;
    }
    if (value === '1410_m_above_sea_level') {
      return `1410 m ${t('common.aboveSeaLevel')}`;
    }
    if (value === '1251_m_above_sea_level') {
      return `1251 m ${t('common.aboveSeaLevel')}`;
    }
    if (value === '1050_m_above_sea_level') {
      return `1050 m ${t('common.aboveSeaLevel')}`;
    }
    if (value === '0_m_above_sea_level') {
      return `0 m ${t('common.aboveSeaLevel')}`;
    }
    
    // Handle population translations
    if (value === '372_updated_on_30_6_2025') {
      return `372 (${t('common.updatedOn')} 30-6-2025)`;
    }
    if (value === '106_updated_on_30_6_2025') {
      return `106 (${t('common.updatedOn')} 30-6-2025)`;
    }
    if (value === '1679_updated_on_30_6_2025') {
      return `1679 (${t('common.updatedOn')} 30-6-2025)`;
    }
    
    // Return original value if no translation needed
    return value;
  };
  const relatedTrips = destinationId ? getTripsForDestination(destinationId) : [];

  // Set page title based on destination data
  usePageTitle(destinationData ? `${destinationData.name} - La Dolce Vita` : undefined);

  // Scroll to top when component mounts or destinationId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [destinationId]);

  // Keyboard navigation for gallery modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isModalOpen || !destinationData) return;
      
      switch (event.key) {
        case 'Escape':
          setIsModalOpen(false);
          break;
        case 'ArrowLeft':
          setSelectedImageIndex(prev => prev === 0 ? destinationData!.gallery.length - 1 : prev - 1);
          break;
        case 'ArrowRight':
          setSelectedImageIndex(prev => prev === destinationData!.gallery.length - 1 ? 0 : prev + 1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, destinationData?.gallery.length]);

  // Determine if arrows are needed based on slider overflow and on resize
  useEffect(() => {
    const evaluateArrows = () => {
      const el = sliderRef.current;
      if (!el) return;
      setShowGalleryArrows(el.scrollWidth > el.clientWidth + 2);
    };
    evaluateArrows();
    window.addEventListener('resize', evaluateArrows);
    return () => window.removeEventListener('resize', evaluateArrows);
  }, [destinationData?.gallery.length]);

  // Helper function to convert medium image path to small image path (.jpg or .webp)
  const getSmallImagePath = (mediumPath: string) => {
    return mediumPath
      .replace('_medium.jpg', '_small.jpg')
      .replace('_medium.webp', '_small.webp');
  };

  // Helper function to get image name from path
  const getImageName = (path: string) => {
    const parts = path.split('/');
    return parts[parts.length - 1]
      .replace('_medium.jpg', '')
      .replace('_medium.webp', '')
      .replace(/_/g, ' ');
  };

  // Helper function to get banner image path
  const getBannerImagePath = (destinationId: string) => {
    return `/destinations/${destinationId}/${destinationId}_banner.webp`;
  };

  // Helper function to get destination name for breadcrumb based on language
  const getDestinationBreadcrumbName = (destinationId: string) => {
    const destinationNameMap: Record<string, Record<string, string>> = {
      'gran_sasso': {
        'en': 'Gran Sasso',
        'it': 'Gran Sasso',
        'nl': 'Gran Sasso'
      },
      'rocca_calascio': {
        'en': 'Rocca Calascio',
        'it': 'Rocca Calascio',
        'nl': 'Rocca Calascio'
      },
      'alba_fucens': {
        'en': 'Alba Fucens',
        'it': 'Alba Fucens',
        'nl': 'Alba Fucens'
      },
      'pescasseroli': {
        'en': 'Pescasseroli',
        'it': 'Pescasseroli',
        'nl': 'Pescasseroli'
      },
      'rocca_di_mezzo': {
        'en': 'Rocca di Mezzo',
        'it': 'Rocca di Mezzo',
        'nl': 'Rocca di Mezzo'
      },
      'collalto_sabino': {
        'en': 'Collalto Sabino',
        'it': 'Collalto Sabino',
        'nl': 'Collalto Sabino'
      },
      'santo_stefano_di_sessanio': {
        'en': 'Santo Stefano di Sessanio',
        'it': 'Santo Stefano di Sessanio',
        'nl': 'Santo Stefano di Sessanio'
      },
      'villetta_barrea': {
        'en': 'Villetta Barrea',
        'it': 'Villetta Barrea',
        'nl': 'Villetta Barrea'
      },
      'pescocostanzo': {
        'en': 'Pescocostanzo',
        'it': 'Pescocostanzo',
        'nl': 'Pescocostanzo'
      },
      'trabocchi_coast': {
        'en': 'Costa dei Trabocchi',
        'it': 'Costa dei Trabocchi',
        'nl': 'Costa dei Trabocchi'
      },
      'torino_di_sangro': {
        'en': 'Torino di Sangro',
        'it': 'Torino di Sangro',
        'nl': 'Torino di Sangro'
      },
      'fossacesia': {
        'en': 'Fossacesia',
        'it': 'Fossacesia',
        'nl': 'Fossacesia'
      },
      'san_vito_chietino': {
        'en': 'San Vito Chietino',
        'it': 'San Vito Chietino',
        'nl': 'San Vito Chietino'
      },
      'ortona': {
        'en': 'Ortona',
        'it': 'Ortona',
        'nl': 'Ortona'
      },
      'punta_aderci': {
        'en': 'Punta Aderci',
        'it': 'Punta Aderci',
        'nl': 'Punta Aderci'
      },
      'vasto': {
        'en': 'Vasto',
        'it': 'Vasto',
        'nl': 'Vasto'
      },
      'campo_imperatore': {
        'en': 'Campo Imperatore',
        'it': 'Campo Imperatore',
        'nl': 'Campo Imperatore'
      },
      'scanno': {
        'en': 'Scanno',
        'it': 'Scanno',
        'nl': 'Scanno'
      }
    };

    const currentLanguage = localStorage.getItem('language') || 'en';
    return destinationNameMap[destinationId]?.[currentLanguage] || destination?.name || destinationId;
  };

  if (!destinationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('common.destinationNotFound')}</h1>
            <p className="text-gray-600 mb-8">{t('common.destinationNotFoundDescription')}</p>
            <Link 
              to="/" 
              className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common.backToHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Type assertion to help TypeScript understand that destinationData is not null
  const destination = destinationData as LocalizedDestinationData;

  const renderOverview = () => (
    <div className="space-y-6">
      {/* First part of description */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-semibold mb-4">{t('common.overview')}</h3>
        <div 
          className="text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: destination.description }}
        />
      </div>

      {/* Image Slider */}
      {destination.gallery && destination.gallery.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-xl font-semibold mb-4">{t('common.gallery')}</h3>
          <div className={`relative ${showGalleryArrows ? 'px-10 md:px-14' : 'px-0'}`}>
            {/* Left Arrow */}
            {showGalleryArrows && (
            <button 
              className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-md transition-all duration-200"
              onClick={() => {
                const slider = document.getElementById('image-slider');
                if (slider) {
                  slider.scrollBy({ left: -200, behavior: 'smooth' });
                }
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            )}

            {/* Right Arrow */}
            {showGalleryArrows && (
            <button 
              className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-600 hover:text-gray-800 p-2 rounded-full shadow-md transition-all duration-200"
              onClick={() => {
                const slider = document.getElementById('image-slider');
                if (slider) {
                  slider.scrollBy({ left: 200, behavior: 'smooth' });
                }
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            )}

            <div 
              id="image-slider"
              className="flex space-x-4 overflow-x-auto pb-4 px-0" 
              ref={sliderRef}
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                ...({ WebkitScrollbar: { display: 'none' } } as any)
              }}
            >
              {destination.gallery.map((imagePath: string, index: number) => (
                <div 
                  key={index}
                  className="flex-shrink-0 cursor-pointer group"
                  onClick={() => {
                    setSelectedImageIndex(index);
                    setIsModalOpen(true);
                  }}
                >
                  <img
                    src={getSmallImagePath(imagePath)}
                    alt={`${destination.name} - ${getImageName(imagePath)}`}
                    className="w-72 h-48 object-cover rounded-lg shadow-md transition-transform duration-200 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Credits removed for destination pages */}
        </div>
      )}

      {/* Second part of description - additional details */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-semibold mb-4">{t('common.additionalDetails')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {destination.additionalDetails && (
            <>
              {destination.additionalDetails.altitude && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('common.altitude')}</h4>
                  <p className="text-gray-600">{translateAdditionalDetail(destination.additionalDetails.altitude)}</p>
                </div>
              )}
              {destination.additionalDetails.surface && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('common.surface')}</h4>
                  <p className="text-gray-600">{translateAdditionalDetail(destination.additionalDetails.surface)}</p>
                </div>
              )}
              {destination.additionalDetails.population && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('common.population')}</h4>
                  <p className="text-gray-600">{translateAdditionalDetail(destination.additionalDetails.population)}</p>
                </div>
              )}
              {destination.additionalDetails.density && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('common.density')}</h4>
                  <p className="text-gray-600">{translateAdditionalDetail(destination.additionalDetails.density)}</p>
                </div>
              )}
              {destination.additionalDetails.length && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">{t('common.length')}</h4>
                  <p className="text-gray-600">{translateAdditionalDetail(destination.additionalDetails.length)}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderHighlights = () => (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h3 className="text-xl font-semibold mb-6">Punti di Interesse</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {destination.highlights.map((highlight: string, index: number) => (
          <div key={index} className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-gray-700">{highlight}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderActivities = () => (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h3 className="text-xl font-semibold mb-6">Attività</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {destination.activities.map((activity: string, index: number) => (
          <div key={index} className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-700">{activity}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMap = () => {
     const getMapIframe = () => {
       const commonProps = {
         width: "100%",
         height: "450",
         style: { border: 0 } as React.CSSProperties,
         allowFullScreen: true as any,
         loading: "lazy" as const,
         referrerPolicy: "no-referrer-when-downgrade" as const,
         title: `${destination.name} - Mappa`,
       };

       const iframe = (src: string) => (
         <iframe
           src={src}
           width={commonProps.width}
           height={commonProps.height}
           style={commonProps.style}
           allowFullScreen
           loading={commonProps.loading}
           referrerPolicy={commonProps.referrerPolicy}
           title={commonProps.title}
         />
       );

      if (destination.id === 'trabocchi_coast') {
        return iframe("https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d11802.32863971199!2d14.43845631625851!3d42.308780405853575!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1scosta%20dei%20trabocchi!5e0!3m2!1sen!2snl!4v1758813987747!5m2!1sen!2snl");
      } else if (destination.id === 'santo_stefano_di_sessanio') {
        return iframe("https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5897.952730278411!2d13.637632068995014!3d42.34302726427026!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13302ca463b21527%3A0x185e0909e8b9ccf1!2s67020%20Santo%20Stefano%20di%20Sessanio%2C%20Province%20of%20L%27Aquila%2C%20Italy!5e0!3m2!1sen!2snl!4v1758813263956!5m2!1sen!2snl");
      } else if (destination.id === 'collalto_sabino') {
        return iframe("https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47322.676839452746!2d13.014194043075953!3d42.157405777104614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132fbc4a8b74e5dd%3A0x4094f9ab239b390!2s02022%20Collalto%20Sabino%2C%20Province%20of%20Rieti%2C%20Italy!5e0!3m2!1sen!2snl!4v1758813114176!5m2!1sen!2snl");
      } else if (destination.id === 'rocca_calascio') {
        return iframe("https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4963.789552726934!2d13.688407247290588!3d42.32665211344088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13302d1937112f1b%3A0x81058dad4b465433!2sRocca%20Calascio!5e0!3m2!1sen!2snl!4v1758813196449!5m2!1sen!2snl");
      } else if (destination.id === 'campo_imperatore') {
        return iframe("https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d229440.26182978533!2d13.542910430346302!3d42.44812447139315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1331d40991875f9f%3A0x251523b39f2f8838!2sCampo%20Imperatore!5e0!3m2!1sen!2snl!4v1758812813419!5m2!1sen!2snl");
      } else if (destination.id === 'scanno') {
        return iframe("https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11877.761302866835!2d13.86909791601632!3d41.90489210793628!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1330131cd3165e5b%3A0x6e06919f6eeb65!2s67038%20Scanno%2C%20Province%20of%20L%27Aquila%2C%20Italy!5e0!3m2!1sen!2snl!4v1758813345022!5m2!1sen!2snl");
      }
      return null;
    };
 
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-semibold mb-6">Mappa</h3>
        <div className="rounded-lg overflow-hidden shadow-sm">
          {getMapIframe()}
        </div>
      </div>
    );
  };

  const renderRelated = () => (
    <div className="space-y-6">
      <RelatedTrips 
        tripIds={relatedTrips} 
        title="Viaggi Correlati"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Header */}
      <div className="relative h-96">
        <img 
          src={getBannerImagePath(destination.id)} 
          alt={destination.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-5xl font-bold mb-2">{destination.name}</h1>
            <p className="text-xl">
              {destination.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-gray-500 hover:text-blue-600">{t('common.home')}</Link>
            <span className="text-gray-400">/</span>
            <Link 
              to="/#destinations" 
              onClick={(e) => {
                e.preventDefault();
                // Naviga alla home e poi scrolla alla sezione destinations
                window.location.href = '/#destinations';
              }}
              className="text-gray-500 hover:text-blue-600 cursor-pointer"
            >
              {t('destinationDetails.breadcrumbDestinations')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {getDestinationBreadcrumbName(destinationId!)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          {/* Mobile: Vertical List */}
          <div className="block sm:hidden">
            <nav className="px-4 py-2">
              {[
                { id: 'overview', label: t('common.overview') },
                { id: 'map', label: t('common.map') },
                { id: 'related', label: t('common.related') }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full text-left py-3 px-4 mb-2 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Desktop: Horizontal Tabs */}
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', label: t('common.overview') },
                  { id: 'map', label: t('common.map') },
                  { id: 'related', label: t('common.related') }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          <div className="p-6">
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'map' && renderMap()}
            {activeTab === 'related' && renderRelated()}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {isModalOpen && destination.gallery && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full h-full max-w-[96vw] max-h-[96vh] px-10 md:px-20">
            {/* Close button on dark area */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 text-white hover:text-gray-300 z-20"
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Side arrows outside the image */}
            {destination.gallery.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex(prev => prev === 0 ? destination.gallery.length - 1 : prev - 1)}
                  className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-20 transition"
                  aria-label="Previous image"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setSelectedImageIndex(prev => prev === destination.gallery.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full z-20 transition"
                  aria-label="Next image"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            <div className="w-full h-full flex items-center justify-center">
              <img 
                src={destination.gallery[selectedImageIndex]} 
                alt={`${destination.name} - ${getImageName(destination.gallery[selectedImageIndex])}`}
                className="max-w-full max-h-[90vh] object-contain"
              />
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm z-20">
              {selectedImageIndex + 1} / {destination.gallery.length}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
} 